import React, { useState, useRef } from 'react';
import { calcOffsetRatio, updatePos } from './scrollCommon';
import Spacer from '../Spacer';
import PropTypes from 'prop-types';
import { fastHalve, fastQuarter } from '../fastMaths';

const ScrollbarDynamic = ({
    id = 'scrollbar-dynamic',
    disabled = false,
    onScroll,
    gutterOffset,
    scrollableLength,
    maxVisibleLength,
    scrollPx = 0,
    isHorizontal = false,
    ...props
}) => {
    const [lastScrollableLength, setLastScrollableLength] = useState(0);
    const [lastMaxVisibleLength, setLastMaxVisibleLength] = useState(0);
    const [scrollbarPos, setScrollbarPos] = useState(0);
    const scrollbarRef = useRef(null);
    const scrollbarLength = fastQuarter(maxVisibleLength);

    if (scrollbarLength > 0 && scrollbarLength > scrollableLength) {
        return isHorizontal ? <Spacer height={20} /> : <Spacer width={20} />;
    }

    if (maxVisibleLength < 1) maxVisibleLength = 1;

    const offsetRatio = calcOffsetRatio(
        scrollableLength,
        maxVisibleLength,
        scrollbarLength
    );

    const moveScrollBar = (Pos) => {
        const maxPx = gutterOffset + maxVisibleLength - scrollbarLength;
        setLastScrollableLength(scrollableLength);
        setLastMaxVisibleLength(maxVisibleLength);
        const newPos = Pos;
        let newPx = scrollPx;

        if (newPos <= gutterOffset) {
            newPx = updatePos(0, offsetRatio);
        } else if (newPos > maxPx) {
            newPx = updatePos(maxPx - gutterOffset, offsetRatio);
        } else {
            newPx = updatePos(newPos - gutterOffset, offsetRatio);
        }
        onScroll(newPx);
    };

    if (
        (lastScrollableLength > scrollableLength ||
            lastMaxVisibleLength !== maxVisibleLength) &&
        scrollPx !== 0
    ) {
        moveScrollBar(scrollbarPos);
    }

    const mouseDown = (e) => {
        if (disabled) return;
        document.addEventListener('mouseup', mouseUp, true);
        document.addEventListener('mousemove', mouseMove, true);
        document.body.style['pointer-events'] = 'none';
        e.preventDefault();
        e.stopPropagation();
    };

    const mouseMove = (e) => {
        if (disabled) return;
        e.stopPropagation();

        const newPos =
            e[isHorizontal ? 'pageX' : 'pageY'] - fastHalve(scrollbarLength);

        setScrollbarPos(newPos);
        moveScrollBar(newPos);
    };

    const mouseUp = (e) => {
        document.removeEventListener('mousemove', mouseMove, true);
        document.removeEventListener('mouseup', mouseUp, true);
        document.body.style['pointer-events'] = 'auto';
        e.stopPropagation();
    };

    return (
        <div
            {...props}
            ref={scrollbarRef}
            id='scrollbar-dynamic'
            style={{
                position: 'relative',
                boxSizing: 'border-box',
                borderRadius: 7.5,
                backgroundColor: '#ffffff',
                ...(disabled
                    ? {
                          opacity: 0,
                          top: isHorizontal ? undefined : scrollPx,
                          left: isHorizontal ? scrollPx : undefined
                      }
                    : {
                          ...(isHorizontal
                              ? {
                                    height: 15,
                                    width: scrollbarLength,
                                    left:
                                        scrollPx > 0
                                            ? scrollPx / offsetRatio
                                            : 0
                                }
                              : {
                                    height: scrollbarLength,
                                    width: 15,
                                    top:
                                        scrollPx > 0
                                            ? scrollPx / offsetRatio
                                            : 0
                                }),
                          cursor: 'grab'
                      })
            }}
            onMouseDown={mouseDown}
        />
    );
};

ScrollbarDynamic.defaultProps = {
    id: 'scrollbar-dynamic',
    disabled: false,
    scrollPx: 0,
    isHorizontal: false
};

ScrollbarDynamic.propTypes = {
    id: PropTypes.string,
    disabled: PropTypes.bool,
    onScroll: PropTypes.func,
    gutterOffset: PropTypes.number,
    scrollableLength: PropTypes.number,
    maxVisibleLength: PropTypes.number,
    scrollPx: PropTypes.number,
    isHorizontal: PropTypes.bool
};

export default ScrollbarDynamic;
